import {
  Article,
  Code,
  ContentCopy,
  EmojiObjects,
  Newspaper,
  Psychology,
  Search,
  Storage,
  Web,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  AvatarGroup,
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Link,
  List,
  ListItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { CopyBlock, dracula } from "react-code-blocks";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supportedLanguages } from "../constants";
import { Employee, Memory, Message } from "../types";
import { countWords } from "../utils";
import LoadingCursor from "./LoadingCursor";
import { StructuredMessageContent } from "./StructuredChatMessage";

// Map memory types to icons
const getMemoryIcon = (memory: Memory) => {
  const type = (memory.memoryType || memory.type || "").toLowerCase();

  if (type.includes("news")) {
    return <Newspaper sx={{ fontSize: 16 }} />;
  } else if (type.includes("research") || type.includes("reasoning")) {
    return <Psychology sx={{ fontSize: 16 }} />;
  } else if (type.includes("memories") || type.includes("memory")) {
    return <EmojiObjects sx={{ fontSize: 16 }} />;
  } else if (type.includes("search") || type.includes("web")) {
    return <Search sx={{ fontSize: 16 }} />;
  } else if (type.includes("browser")) {
    return <Web sx={{ fontSize: 16 }} />;
  } else if (type.includes("code") || type.includes("execute")) {
    return <Code sx={{ fontSize: 16 }} />;
  } else if (type.includes("database") || type.includes("storage")) {
    return <Storage sx={{ fontSize: 16 }} />;
  } else if (type.includes("document")) {
    return <Article sx={{ fontSize: 16 }} />;
  }
  return <EmojiObjects sx={{ fontSize: 16 }} />;
};

// Build blocks from text and attachments for user messages
const buildUserMessageBlocks = (message: any) => {
  const blocks: any[] = [];

  // Add text block
  if (message.text) {
    blocks.push({
      type: 'text',
      content: message.text,
    });
  }

  // Add attachments block if present
  if (message.attachments && message.attachments.length > 0) {
    blocks.push({
      type: 'attachments',
      attachments: message.attachments,
    });
  }

  return blocks;
};

const ChatMessage = React.memo(
  ({
    message,
    mode,
    getMessageBorderRadius,
    getMessageAlign,
    getBackgroundColor,
    getTooltipBlock,
    copyContent,
    setDialogSelectedMessage,
    employees,
  }: {
    message: any;
    mode: any;
    getMessageBorderRadius: any;
    getMessageAlign: any;
    getBackgroundColor: any;
    getTooltipBlock: any;
    copyContent: any;
    setDialogSelectedMessage: any;
    employees?: Employee[];
  }) => {
    // For user messages, build blocks from text + attachments
    // For assistant messages, use the stored blocks
    const effectiveBlocks = message.role === 'user' && message.attachments?.length > 0
      ? buildUserMessageBlocks(message)
      : message.blocks;

    return (
      <ListItem
        style={{
          padding: "0px, auto",
        }}
      >
        {getMessageAlign(message.role) === "right" && (
          <div style={{ flex: "1" }}></div>
        )}
        <Card
          variant="outlined"
          style={{
            borderRadius: getMessageBorderRadius(message.role),
            float: getMessageAlign(message.role),
            backgroundColor: getBackgroundColor(message),
            border: "none",
          }}
        >
          <CardHeader
            style={{
              float: getMessageAlign(message.role),
              width: "100%",
            }}
            avatar={
              <Avatar
                alt={message.user}
                sx={{ width: 30, height: 30 }}
                src={message.picture}
              />
            }
            title={
              <div>
                {message.user}
                <Tooltip title="Copy message to clipboard">
                  <IconButton
                    color="primary"
                    style={{
                      float: "right",
                      fontSize: 10,
                      marginLeft: 10,
                    }}
                    onClick={() => copyContent(message.text)}
                  >
                    <ContentCopy sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>
              </div>
            }
            subheader={moment(new Date(message.ts)).fromNow()}
          />
          <CardContent>
            {message.text.startsWith("```") && <p>Code Block</p>}
            <br />
            {message.text === "" &&
              (!effectiveBlocks || effectiveBlocks.length === 0) && (
                <p style={{ marginTop: 25 }}>
                  <LoadingCursor
                    loading={message.loading}
                    children={message.text}
                  />
                </p>
              )}
            {effectiveBlocks && effectiveBlocks.length > 0 ? (
              <StructuredMessageContent
                blocks={effectiveBlocks}
                loading={message.loading}
                messageId={message.id}
                employees={employees}
              />
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                children={message.text.trim()}
                components={{
                  a(props) {
                    const { href, children } = props;
                    return (
                      <Link
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {children}
                      </Link>
                    );
                  },
                  p(props) {
                    const { children, ...rest } = props;
                    // Check if this is the last element in the message
                    const isLastElement =
                      props.node &&
                      props.node.position &&
                      props.node.position.end &&
                      props.node.position.end.offset ===
                        message.text.trim().length;
                    return (
                      <p {...rest}>
                        {children}
                        {isLastElement && message.loading && (
                          <LoadingCursor loading={true}>{""}</LoadingCursor>
                        )}
                      </p>
                    );
                  },
                  code(props) {
                    const { children, className, node, inline, ...rest } =
                      props;

                    if (inline) {
                      // Check if this is a tool call (contains wrench emoji)
                      const isToolCall = children.includes("🔧");

                      return (
                        <>
                          <code
                            style={{
                              fontFamily:
                                '"SFMono-Regular", Menlo, Consolas, "PT Mono", "Liberation Mono", Courier, monospace',
                              lineHeight: "normal",
                              background: "rgb(243 244 246",
                              color: "#000000",
                              borderRadius: "4px",
                              fontSize: "85%",
                              padding: "0.25em 0.5em",
                              borderColor: "hsl(0 0% 89.8%)",
                              borderWidth: 1,
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            {children}
                          </code>
                          {isToolCall && <br />}
                        </>
                      );
                    }
                    const match = /language-(\w+)/.exec(className || "");
                    var lang = "";
                    if (match && (match[1] == "sh" || match[1] == "bash")) {
                      match[1] = "shell";
                    }
                    if (match && supportedLanguages.includes(match[1])) {
                      lang = match[1];
                    } else {
                      lang = "text";
                    }
                    return (
                      <CopyBlock
                        language={lang}
                        text={String(children).replace(/\n$/, "")}
                        showLineNumbers
                        theme={dracula}
                        wrapLines
                        codeBlock
                      />
                    );
                  },
                }}
              />
            )}
            <div
              style={{
                width: "100%",
                display: "flex",
              }}
            >
              {getTooltipBlock(message)}
              <div style={{ flex: "1" }}></div>
              <Typography variant="caption" color="textSecondary">
                {countWords(message.text)} words
              </Typography>
            </div>
          </CardContent>
        </Card>
      </ListItem>
    );
  }
);

export default ({
  chat,
  eventListener,
  mode,
  setDialogSelectedMessage,
  autoScroll = true,
  employees,
}: {
  mode: string;
  chat: Message[];
  eventListener: Function;
  setDialogSelectedMessage: (msg: Message) => void;
  autoScroll?: boolean;
  employees?: Employee[];
}) => {
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize functions to prevent recreating them on each render
  const copyContent = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        eventListener({
          type: "SET_INFO",
          info: "Content copied to clipboard",
        });
      } catch (err) {
        eventListener({
          type: "SET_ERROR",
          error: "Error copying to clipboard",
        });
      }
    },
    [eventListener]
  );

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && messagesEndRef.current.scrollIntoView) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }
  }, []);

  useEffect(() => {
    if (autoScroll) {
      // Throttle scroll updates to make it smoother - only scroll if not already scrolling
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        scrollToBottom();
      }, 50); // Small delay to batch rapid updates
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [chat, autoScroll, scrollToBottom]);

  // Memoize these functions since they're used in message rendering
  const getMessageBorderRadius = useCallback((role: String) => {
    return "20px";
  }, []);

  const getMessageAlign = useCallback((role: String) => {
    return role !== "user" ? "left" : "right";
  }, []);

  const getBackgroundColor = useCallback(
    (message: Message) => {
      if (message.role === "user") {
        return mode === "dark"
          ? "#0a0a0a"
          : alpha(theme.palette.primary.light, 0.05);
      } else {
        return mode === "dark"
          ? "transparent"
          : alpha(theme.palette.grey[100], 0.2);
      }
    },
    [mode, theme]
  );

  const getTooltipBlock = useCallback(
    (message: Message) => {
      if (
        !message.context ||
        !message.context.memories ||
        message.context.memories.length === 0
      ) {
        return;
      }
      return (
        <Tooltip title="View context sources">
          <AvatarGroup
            max={4}
            sx={{
              "& .MuiAvatar-root": { width: 24, height: 24, cursor: "pointer" },
            }}
            onClick={() => {
              setDialogSelectedMessage(message);
            }}
          >
            {message.context.memories.map((memory: Memory, idx: number) => (
              <Avatar
                key={idx}
                sx={{
                  width: 24,
                  height: 24,
                }}
              >
                {getMemoryIcon(memory)}
              </Avatar>
            ))}
          </AvatarGroup>
        </Tooltip>
      );
    },
    [setDialogSelectedMessage]
  );

  const messagesList = useMemo(() => {
    return chat.map((message: Message, index: number) => (
      <ChatMessage
        key={message.id}
        message={message}
        mode={mode}
        getMessageBorderRadius={getMessageBorderRadius}
        getMessageAlign={getMessageAlign}
        getBackgroundColor={getBackgroundColor}
        getTooltipBlock={getTooltipBlock}
        copyContent={copyContent}
        setDialogSelectedMessage={setDialogSelectedMessage}
        employees={employees}
      />
    ));
  }, [
    chat,
    mode,
    getMessageBorderRadius,
    getMessageAlign,
    getBackgroundColor,
    getTooltipBlock,
    copyContent,
    setDialogSelectedMessage,
    employees,
  ]);

  return (
    <Box>
      <List>{messagesList}</List>
      <Box ref={messagesEndRef} />
    </Box>
  );
};
